import React, { useState, useEffect } from 'react';
import { Spinner, Card } from 'react-bootstrap';
import { FaCheckCircle, FaCreditCard } from 'react-icons/fa';
import { MdErrorOutline } from 'react-icons/md';
import CommonModal from '../../common/CommonModal';
import ModalFooter from '../../common/ModalFooter';
import { previewChangePlan } from '../../services/subscription';
import PropTypes from 'prop-types';

const UpgradeConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  targetPlan,
  subscription,
  isProcessing
}) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!show || !targetPlan) return;
      try {
        setLoading(true);
        setError(null);
        const res = await previewChangePlan(targetPlan.id);
        if (res.data?.success && res.data?.data) {
          setPreviewData(res.data.data);
        } else {
          setError('Failed to load pricing preview.');
        }
      } catch (err) {
        console.error('Error fetching preview:', err);
        setError('Failed to load pricing preview.');
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [show, targetPlan]);

  const pm = subscription?.current_payment_method || subscription?.default_payment_method || {};
  const brand = subscription?.card_brand || pm.brand || 'Card';
  const last4 = subscription?.card_last4 || pm.last4 || '****';
  const expMonth = subscription?.card_exp_month || pm.exp_month || '**';
  const expYear = subscription?.card_exp_year || pm.exp_year || '****';

  // Format currency
  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  // Helper to get features unlocked
  const getFeaturesUnlocked = () => {
    // Basic static mapping based on plan codes for now, matching the Figma
    if (targetPlan?.code?.toUpperCase() === 'GOLD') {
      return [
        'Add up to 50 auditors',
        'Clone & create up to 15 templates',
        'All dashboard & advanced charts',
        'Create up to 5 delegate admin roles'
      ];
    } else if (targetPlan?.code?.toUpperCase() === 'SILVER') {
      return [
        'Add up to 10 auditors',
        'Clone & create up to 3 templates',
        'Create up to 1 delegate admin role'
      ];
    }
    return [];
  };

  const unlockedFeatures = getFeaturesUnlocked();

  return (
    <CommonModal
      show={show}
      type="upgrade"
      title={`Upgrade To ${targetPlan?.name || 'New Plan'}`}
      subtitle="You&apos;ll be charged the prorated difference immediately."
      disableFocusTrap={false}
      onClose={onHide}
    >
      <div className="upgrade-summary-content text-start mt-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#005457' }} />
            <p className="mt-3 text-muted">Calculating pricing...</p>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <MdErrorOutline size={32} className="mb-2" />
            <p>{error}</p>
          </div>
        ) : previewData ? (
          <>
            {/* Proration Breakdown */}
            <Card className="border-0 bg-light mb-3 text-start" style={{ borderRadius: '8px' }}>
              <Card.Body className="p-4">
                <h6 style={{ color: '#212121', fontWeight: 700, fontSize: '16px' }} className="mb-3">Pricing Summary:</h6>
                
                {previewData.pricing_summary?.credit_amount > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>Unused time on current plan</span>
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>
                      -{formatCurrency(previewData.pricing_summary.credit_amount, previewData.pricing_summary.currency)}
                    </span>
                  </div>
                )}

                {previewData.pricing_summary?.upgrade_amount > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>Remaining time on new plan</span>
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>
                      +{formatCurrency(previewData.pricing_summary.upgrade_amount, previewData.pricing_summary.currency)}
                    </span>
                  </div>
                )}

                {previewData.pricing_summary?.discount_amount > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>Discount</span>
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>
                      -{formatCurrency(previewData.pricing_summary.discount_amount, previewData.pricing_summary.currency)}
                    </span>
                  </div>
                )}

                {previewData.pricing_summary?.tax_amount > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>Tax</span>
                    <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>
                      +{formatCurrency(previewData.pricing_summary.tax_amount, previewData.pricing_summary.currency)}
                    </span>
                  </div>
                )}
                
                <hr className="my-2" />
                
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span style={{ color: '#333333', fontWeight: 400, fontSize: '15px' }}>Total charged today</span>
                  <span style={{ color: '#212121', fontWeight: 700, fontSize: '15px' }}>
                    {formatCurrency(previewData.pricing_summary?.amount_due_now, previewData.pricing_summary?.currency)}
                  </span>
                </div>
              </Card.Body>
            </Card>

            {/* Features You'll Unlock */}
            {unlockedFeatures.length > 0 && (
              <Card className="border-0 bg-light mb-3 text-start" style={{ borderRadius: '8px' }}>
                <Card.Body className="p-4">
                  <h6 style={{ color: '#212121', fontWeight: 700, fontSize: '16px' }} className="mb-3">Features You&apos;ll Unlock:</h6>
                  <ul className="list-unstyled m-0">
                    {unlockedFeatures.map((f, idx) => (
                      <li key={idx} className="d-flex align-items-center mb-2" style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>
                        <FaCheckCircle className="me-2 text-success" /> {f}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            )}

            {/* Payment Method */}
            <Card className="border shadow-sm mb-2 text-start" style={{ borderRadius: '8px' }} role="region" aria-label="Payment Method">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>Card</span>
                  <span className="d-flex align-items-center" style={{ color: '#212121', fontWeight: 700, fontSize: '14px' }} aria-label={`${brand} ending in ${last4}`}>
                    <FaCreditCard className="me-2" style={{ color: '#333333' }} aria-hidden="true" /> {brand} ending in {last4}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ color: '#333333', fontWeight: 400, fontSize: '14px' }}>Expires</span>
                  <span style={{ color: '#212121', fontWeight: 700, fontSize: '14px' }} aria-label={`Expires ${String(expMonth).padStart(2, '0')} / ${expYear}`}>{String(expMonth).padStart(2, '0')} / {expYear}</span>
                </div>
              </Card.Body>
            </Card>

            {/* Modal Footer with Actions */}
            <ModalFooter
              onCancel={onHide}
              onSubmit={onConfirm}
              submitLabel="Confirm & Pay"
              loading={isProcessing}
            />
          </>
        ) : null}
      </div>
    </CommonModal>
  );
};

UpgradeConfirmationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  targetPlan: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string
  }),
  subscription: PropTypes.shape({
    current_payment_method: PropTypes.object,
    default_payment_method: PropTypes.object,
    card_brand: PropTypes.string,
    card_last4: PropTypes.string,
    card_exp_month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    card_exp_year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  isProcessing: PropTypes.bool
};

export default UpgradeConfirmationModal;
